/**
 * Icon Picker Component
 *
 * Allows selection of FontAwesome icons for map markers.
 */

import React, { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFire,
  faTree,
  faCloud,
  faCloudRain,
  faSnowflake,
  faSun,
  faBolt,
  faWind,
  faWater,
  faMountain,
  faMapMarkerAlt,
  faMapPin,
  faLocationDot,
  faCircle,
  faSquare,
  faStar,
  faHeart,
  faFlag,
  faHome,
  faBuilding,
  faIndustry,
  faTruck,
  faPlane,
  faHelicopter,
  faShip,
  faAnchor,
  faCar,
  faGasPump,
  faChargingStation,
  faHospital,
  faSchool,
  faChurch,
  faCampground,
  faTent,
  faPersonHiking,
  faBinoculars,
  faCamera,
  faTriangleExclamation,
  faCircleExclamation,
  faCircleInfo,
  faCircleCheck,
  faCircleXmark,
  faRadiation,
  faBiohazard,
  faSkullCrossbones,
  faLeaf,
  faSeedling,
  faDroplet,
  faTemperatureHigh,
  faTemperatureLow,
  faGauge,
  faTowerBroadcast,
  faSatelliteDish,
  faLightbulb,
  faBell,
  faExclamation,
  faQuestion,
  IconDefinition,
} from '@fortawesome/free-solid-svg-icons';
import './IconPicker.scss';

// Icon categories with their icons
const iconCategories: Record<string, { icon: IconDefinition; name: string }[]> = {
  'Fire & Weather': [
    { icon: faFire, name: 'fire' },
    { icon: faBolt, name: 'bolt' },
    { icon: faCloud, name: 'cloud' },
    { icon: faCloudRain, name: 'cloud-rain' },
    { icon: faSnowflake, name: 'snowflake' },
    { icon: faSun, name: 'sun' },
    { icon: faWind, name: 'wind' },
    { icon: faTemperatureHigh, name: 'temperature-high' },
    { icon: faTemperatureLow, name: 'temperature-low' },
    { icon: faDroplet, name: 'droplet' },
  ],
  'Nature': [
    { icon: faTree, name: 'tree' },
    { icon: faLeaf, name: 'leaf' },
    { icon: faSeedling, name: 'seedling' },
    { icon: faMountain, name: 'mountain' },
    { icon: faWater, name: 'water' },
  ],
  'Markers': [
    { icon: faMapMarkerAlt, name: 'map-marker-alt' },
    { icon: faMapPin, name: 'map-pin' },
    { icon: faLocationDot, name: 'location-dot' },
    { icon: faFlag, name: 'flag' },
    { icon: faStar, name: 'star' },
  ],
  'Shapes': [
    { icon: faCircle, name: 'circle' },
    { icon: faSquare, name: 'square' },
    { icon: faHeart, name: 'heart' },
    { icon: faExclamation, name: 'exclamation' },
    { icon: faQuestion, name: 'question' },
  ],
  'Buildings': [
    { icon: faHome, name: 'home' },
    { icon: faBuilding, name: 'building' },
    { icon: faIndustry, name: 'industry' },
    { icon: faHospital, name: 'hospital' },
    { icon: faSchool, name: 'school' },
    { icon: faChurch, name: 'church' },
  ],
  'Transport': [
    { icon: faTruck, name: 'truck' },
    { icon: faPlane, name: 'plane' },
    { icon: faHelicopter, name: 'helicopter' },
    { icon: faShip, name: 'ship' },
    { icon: faCar, name: 'car' },
    { icon: faAnchor, name: 'anchor' },
  ],
  'Outdoor': [
    { icon: faCampground, name: 'campground' },
    { icon: faTent, name: 'tent' },
    { icon: faPersonHiking, name: 'person-hiking' },
    { icon: faBinoculars, name: 'binoculars' },
    { icon: faCamera, name: 'camera' },
  ],
  'Status': [
    { icon: faTriangleExclamation, name: 'triangle-exclamation' },
    { icon: faCircleExclamation, name: 'circle-exclamation' },
    { icon: faCircleInfo, name: 'circle-info' },
    { icon: faCircleCheck, name: 'circle-check' },
    { icon: faCircleXmark, name: 'circle-xmark' },
    { icon: faBell, name: 'bell' },
  ],
  'Infrastructure': [
    { icon: faGasPump, name: 'gas-pump' },
    { icon: faChargingStation, name: 'charging-station' },
    { icon: faTowerBroadcast, name: 'tower-broadcast' },
    { icon: faSatelliteDish, name: 'satellite-dish' },
    { icon: faGauge, name: 'gauge' },
    { icon: faLightbulb, name: 'lightbulb' },
  ],
  'Hazard': [
    { icon: faRadiation, name: 'radiation' },
    { icon: faBiohazard, name: 'biohazard' },
    { icon: faSkullCrossbones, name: 'skull-crossbones' },
  ],
};

// Flat list of all icons for search
const allIcons = Object.values(iconCategories).flat();

// Icon name to definition map
export const iconNameToDefinition: Record<string, IconDefinition> = {};
allIcons.forEach(({ icon, name }) => {
  iconNameToDefinition[name] = icon;
});

interface IconPickerProps {
  value: string;
  onChange: (iconName: string) => void;
  color?: string;
}

export const IconPicker: React.FC<IconPickerProps> = ({ value, onChange, color = '#3b82f6' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  // Filter icons based on search
  const filteredIcons = useMemo(() => {
    if (!search.trim()) {
      return activeCategory ? { [activeCategory]: iconCategories[activeCategory] } : iconCategories;
    }

    const searchLower = search.toLowerCase();
    const filtered: Record<string, { icon: IconDefinition; name: string }[]> = {};

    Object.entries(iconCategories).forEach(([category, icons]) => {
      const matches = icons.filter(({ name }) => name.includes(searchLower));
      if (matches.length > 0) {
        filtered[category] = matches;
      }
    });

    return filtered;
  }, [search, activeCategory]);

  const selectedIcon = iconNameToDefinition[value];

  return (
    <div className="icon-picker">
      <button
        type="button"
        className="icon-picker-trigger"
        onClick={() => setIsOpen(!isOpen)}
        style={{ color }}
      >
        {selectedIcon ? (
          <FontAwesomeIcon icon={selectedIcon} />
        ) : (
          <span className="no-icon">Select icon</span>
        )}
        <span className="icon-name">{value || 'None'}</span>
        <span className="dropdown-arrow">▼</span>
      </button>

      {isOpen && (
        <div className="icon-picker-dropdown">
          <div className="icon-picker-search">
            <input
              type="text"
              placeholder="Search icons..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </div>

          <div className="icon-picker-categories">
            <button
              type="button"
              className={`category-btn ${activeCategory === null ? 'active' : ''}`}
              onClick={() => setActiveCategory(null)}
            >
              All
            </button>
            {Object.keys(iconCategories).map((category) => (
              <button
                key={category}
                type="button"
                className={`category-btn ${activeCategory === category ? 'active' : ''}`}
                onClick={() => setActiveCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>

          <div className="icon-picker-grid">
            {Object.entries(filteredIcons).map(([category, icons]) => (
              <div key={category} className="icon-category">
                {!activeCategory && <div className="category-label">{category}</div>}
                <div className="icons-row">
                  {icons.map(({ icon, name }) => (
                    <button
                      key={name}
                      type="button"
                      className={`icon-btn ${value === name ? 'selected' : ''}`}
                      onClick={() => {
                        onChange(name);
                        setIsOpen(false);
                      }}
                      title={name}
                      style={{ color: value === name ? color : undefined }}
                    >
                      <FontAwesomeIcon icon={icon} />
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="icon-picker-footer">
            <button
              type="button"
              className="clear-btn"
              onClick={() => {
                onChange('');
                setIsOpen(false);
              }}
            >
              Clear Selection
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default IconPicker;
